// src/config/auth.ts

/**
 * Authentication Configuration
 * Handles switching between Firebase and API authentication with multi-role support
 */

import { UserType } from '../types/auth.types';

export interface AuthConfig {
  provider: 'firebase' | 'api';
  apiBaseUrl?: string;
  endpoints: {
    defaultAdministrator: string;
    administrator: string;
    driver: string;
    passenger: string;
  };
  passwordResetEndpoints: {
    // email: string;
    // otp: string;
    // defaultAdministrator: string;
    administrator: string;
    driver: string;
    passenger: string;
  };
  forgotPasswordEndpoints:{
    // defaultAdministrator: string;
    administrator: string;
    driver: string;
    passenger: string;
  }
  refreshTokenEndpoints:{
    // defaultAdministrator: string;
    administrator: string;
    driver: string;
    passenger: string;
  }
}

// Authentication configuration - can be toggled via environment variable
export const authConfig: AuthConfig = {
  // provider: (import.meta.env.VITE_AUTH_PROVIDER as 'firebase' | 'api') || 'api',
  provider: 'api',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'https://transition-api.ubuntucab.com',
  endpoints: {
    defaultAdministrator: '/auth/default-administrator/login',
    administrator: '/auth/administrator/login',
    driver: '/auth/driver/signin',
    passenger: '/auth/passenger/signin',
  },
  passwordResetEndpoints: {
    // email: '/auth/reset-password/email',
    // otp: '/auth/reset-password/otp',
    administrator: '/auth/administrator/reset-password',
    driver: '/auth/driver/reset-password',
    passenger: '/auth/passenger/reset-password',
  },
  forgotPasswordEndpoints: {
    administrator: '/auth/administrator/forgot-password',
    driver: '/auth/driver/forgot-password',
    passenger: '/auth/passenger/forgot-password',
  },
  refreshTokenEndpoints: {
    administrator: '/auth/administrator/refresh-token',
    driver: '/auth/driver/refresh-token',
    passenger: '/auth/passenger/refresh-token',
  },
};

// API response types for authentication
export interface ApiAuthResponse {
  email: string;
  accessToken: string;
}

export interface ApiLoginRequest {
  email: string;
  password: string;
}

// Multi-role token storage utilities
export const tokenStorage = {
  getToken: (): string | null => {
    return localStorage.getItem('accessToken');
  },
  
  setToken: (token: string): void => {
    localStorage.setItem('accessToken', token);
  },
  
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken');
  },
  
  setRefreshToken: (token: string): void => {
    localStorage.setItem('refreshToken', token);
  },
  
  removeTokens: (): void => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userIdentifier');
    localStorage.removeItem('userType');
    localStorage.removeItem('userProfile');
  },
  
  getUserEmail: (): string | null => {
    return localStorage.getItem('userEmail');
  },
  
  setUserEmail: (email: string): void => {
    localStorage.setItem('userEmail', email);
  },
  
  getUserIdentifier: (): string | null => {
    return localStorage.getItem('userIdentifier');
  },
  
  setUserIdentifier: (identifier: string): void => {
    localStorage.setItem('userIdentifier', identifier);
  },
  
  getUserType: (): UserType | null => {
    return localStorage.getItem('userType') as UserType | null;
  },
  
  setUserType: (userType: UserType): void => {
    localStorage.setItem('userType', userType);
  },
  
  getUserProfile: (): string | null => {
    return localStorage.getItem('userProfile');
  },
  
  setUserProfile: (profile: string): void => {
    localStorage.setItem('userProfile', profile);
  },
  
  hasRefreshToken: (): boolean => {
    return !!localStorage.getItem('refreshToken');
  },
  
  // Legacy support
  removeToken: (): void => {
    tokenStorage.removeTokens();
  }
};

// Token validation utility
export const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;
  
  try {
    // Basic JWT structure check (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    // Decode payload to check expiration
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token is expired (if exp claim exists)
    if (payload.exp && payload.exp < currentTime) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

// Export configuration for easy access
export default authConfig;